import React, {useEffect, useMemo, useState} from 'react';
import {AiMentionsChatDemoContent} from "../../models/AiMentionsContent";

// A decorative, looping "conversation with an AI" panel: the question types out,
// three dots think, then the answer types out with the runs the admin wrapped in
// *asterisks* highlighted. No API call, no user input.
//
// Ported from the handoff's vanilla-JS reference loop, with four things the
// reference did not do: every timer is cancelled on unmount, the loop pauses
// while the tab is hidden, prefers-reduced-motion renders the final frame with
// no animation at all, and the panel has a fixed floor so nothing below it moves
// while the bubbles grow.

const QUESTION_STEP_MS = 28;
const BEFORE_THINKING_MS = 500;
const THINKING_MS = 1100;
const ANSWER_STEP_MS = 14;
const LOOP_PAUSE_MS = 6500;

type Phase = 'question' | 'thinking' | 'answer';

interface Frame {
    phase: Phase;
    // characters typed so far, for the typing phases
    n: number;
}

export interface AnswerSegment {
    text: string;
    // rendered as the highlighted span - the admin wrapped it in asterisks
    bold: boolean;
}

// A *wrapped* run is the emphasis marker the admin authors with. Anything the
// pattern does not pair up - a lone asterisk, an empty ** - is left alone and
// types out literally, so a half-finished edit never breaks the panel.
const MARKER = /\*([^*]+)\*/g;

// THE single source of truth for how an authored answer becomes rendered runs:
// the animation, the reduced-motion frame and the transcript all read it.
export const answerSegments = (answer: string): AnswerSegment[] => {
    const segments: AnswerSegment[] = [];
    const marker = new RegExp(MARKER.source, MARKER.flags);
    let at = 0;
    let match = marker.exec(answer);

    while (match !== null) {
        if (match.index > at) {
            segments.push({text: answer.slice(at, match.index), bold: false});
        }
        segments.push({text: match[1], bold: true});
        at = match.index + match[0].length;
        match = marker.exec(answer);
    }

    if (at < answer.length) {
        segments.push({text: answer.slice(at), bold: false});
    }

    return segments;
};

// What the visitor actually reads: the answer with the markers taken out. The
// typing counts characters of THIS, not of the authored string.
export const answerText = (segments: AnswerSegment[]): string =>
    segments.map(segment => segment.text).join('');

const FIRST_FRAME: Frame = {phase: 'question', n: 1};

const nextFrame = (frame: Frame, questionLength: number, answerLength: number): { frame: Frame; delay: number } => {
    if (frame.phase === 'question') {
        return frame.n < questionLength
            ? {frame: {phase: 'question', n: frame.n + 1}, delay: QUESTION_STEP_MS}
            : {frame: {phase: 'thinking', n: 0}, delay: BEFORE_THINKING_MS};
    }

    if (frame.phase === 'thinking') {
        return {frame: {phase: 'answer', n: 1}, delay: THINKING_MS};
    }

    return frame.n < answerLength
        ? {frame: {phase: 'answer', n: frame.n + 1}, delay: ANSWER_STEP_MS}
        // ...and round again
        : {frame: FIRST_FRAME, delay: LOOP_PAUSE_MS};
};

const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Caret: React.FC = () => <span className="lnk-aim__caret">|</span>;

interface ChatDemoProps {
    content: AiMentionsChatDemoContent;
}

const ChatDemo: React.FC<ChatDemoProps> = ({content}) => {
    const {question, answer} = content;
    const segments = useMemo(() => answerSegments(answer), [answer]);
    // the markers are authoring syntax, never characters the visitor waits for
    const plainAnswer = useMemo(() => answerText(segments), [segments]);

    // Read once: a visitor who asked for reduced motion never gets the loop, not
    // even a first frame of it.
    const [reducedMotion] = useState(prefersReducedMotion);
    const [frame, setFrame] = useState<Frame>(FIRST_FRAME);
    const [paused, setPaused] = useState<boolean>(() => typeof document !== 'undefined' && document.hidden);

    useEffect(() => {
        if (reducedMotion) {
            return;
        }

        const onVisibilityChange = () => setPaused(document.hidden);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [reducedMotion]);

    useEffect(() => {
        // A hidden tab burns no timers; the loop resumes from wherever it stopped.
        if (reducedMotion || paused) {
            return;
        }

        const next = nextFrame(frame, question.length, plainAnswer.length);
        const timer = window.setTimeout(() => setFrame(next.frame), next.delay);

        return () => window.clearTimeout(timer);
    }, [frame, paused, reducedMotion, question, plainAnswer]);

    // Reduced motion is the finished frame, whatever the copy is now - deriving
    // it from a character count taken at mount would truncate a longer answer
    // that arrived with the published content.
    const showAnswer = reducedMotion || frame.phase === 'answer';
    const typed = reducedMotion ? plainAnswer.length : frame.n;

    // Every segment is styled as it appears: a highlighted run is highlighted
    // from its first character, not once the typing has passed it.
    const renderAnswer = () => {
        let start = 0;

        const runs = segments.map((segment, index) => {
            const visible = segment.text.slice(0, Math.max(typed - start, 0));
            start += segment.text.length;

            if (!visible) {
                return null;
            }
            return segment.bold
                ? <strong key={index} className="lnk-aim__brand">{visible}</strong>
                : <React.Fragment key={index}>{visible}</React.Fragment>;
        });

        return (
            <>
                {runs}
                {typed < plainAnswer.length && <Caret/>}
            </>
        );
    };

    return (
        <div className="lnk-aim__chat">
            <div className="lnk-aim__chat-bar">
                <span className="lnk-aim__chat-mark" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="#fff" aria-hidden="true">
                        <path d="M12 2l3 6 6 3-6 3-3 6-3-6-6-3 6-3z"/>
                    </svg>
                </span>
                <span>שיחה עם AI</span>
            </div>

            {/* The animation itself is decoration; the transcript below carries
                the same content for assistive tech, without the churn. */}
            <div className="lnk-aim__chat-body" aria-hidden="true">
                <div className="lnk-aim__bubble--question">
                    {!reducedMotion && frame.phase === 'question' ? (
                        <>
                            {question.slice(0, frame.n)}
                            <Caret/>
                        </>
                    ) : question}
                </div>

                {!reducedMotion && frame.phase === 'thinking' && (
                    <div className="lnk-aim__bubble--answer lnk-aim__typing">
                        <span/><span/><span/>
                    </div>
                )}

                {showAnswer && (
                    <div className="lnk-aim__bubble--answer">{renderAnswer()}</div>
                )}
            </div>

            <div className="lnk-aim__sr-only">
                <p>{`שאלה לדוגמה: ${question}`}</p>
                <p>{`תשובה לדוגמה: ${plainAnswer}`}</p>
            </div>
        </div>
    );
};

export default ChatDemo;
